// frontend/src/components/homepage/CtaLive.js
//
// The final CTA - one glass panel with a slow aurora sweep behind it, three
// decorative rings, and a single ember button.
//
// The button is a RouterLink to /register: the prototype's `href="#"` is a
// placeholder, and /register is where both the production FinalCTASection and
// the ported hero's primary CTA already point. The prototype's `data-magnet`
// pointer-follow is NOT ported, on the hero's precedent (HeroLive carries the
// same attribute in the prototype and ships without it).
//
// The aurora is pure CSS (`hl-sweep`, 24s linear) and stops with every other
// animation under the motion-off block in homepage.css; its base state - the
// conic gradient at 0deg - is what stays.
//
// `CtaBandLive` (named export, owner round 2) is the COMPACT band that sits
// under the blog, the last thing before the footer: the same panel language
// (glass, hairline, shadow) in one line - an <h3> with the SAME wording and
// the same button beside it. An <h3>, not <h2>: the main CTA above already
// owns "הצעד הבא שלך מתחיל כאן." as the section's H2, and the page keeps one
// H2 per section. No sub-line, no rings, no blurred aurora - a static conic
// wash is the whole background, so the band costs nothing to paint.

import React from "react";
import { Link as RouterLink } from "react-router-dom";
import SectionShell from "./SectionShell";
import Arrow from "./Arrow";
import useReveal from "../../motion/useReveal";
import "../../homepage/typesNumbersCta.css";

export const CTA_TARGET = "/register";

export const COPY = {
  he: {
    eyebrow: "מוכן להתחיל?",
    heading: "הצעד הבא שלך מתחיל כאן.",
    sub: "הצטרף עכשיו והתחל לקבל הזדמנויות כבר היום.",
    button: "הצטרף",
  },
  en: {
    eyebrow: "Ready to start?",
    heading: "Your next step starts here.",
    sub: "Join now and start receiving opportunities today.",
    button: "Join",
  },
};

/**
 * The compact closing band. Same copy, same target, one line.
 *
 * Both copy props are OPTIONAL and default to today's constants, so
 * `Homepage.js` - which passes neither - renders exactly what it did before
 * they existed. A profession page passes its own line.
 *
 * @param {object} props
 * @param {boolean} [props.isHebrew]
 * @param {string} [props.title] the band's single line (the <h3>)
 * @param {string} [props.ctaLabel] the button's label
 * @param {string} [props.trackSection] engagement section name (see
 *   SectionShell). UNSET by default: the band is untagged unless its page
 *   names it, so it never double-counts under the closing panel's name.
 */
export function CtaBandLive({ isHebrew = true, title, ctaLabel, trackSection }) {
  const t = isHebrew ? COPY.he : COPY.en;
  const heading = title ?? t.heading;
  const button = ctaLabel ?? t.button;
  const panel = useReveal();

  return (
    <SectionShell
      scene="cta"
      mesh="cloud"
      meshAlpha=".5"
      className="hl-cta hl-ctab"
      trackSection={trackSection}
      // `padding: 40px 0 96px` at every width - `pb` after `py`, as above -
      // scaled by the owner's round-3 -20% (40 -> 32, 96 -> 77).
      sx={{ py: "32px", pb: "77px" }}
    >
      <div
        ref={panel.ref}
        className={panel.className ? `hl-ctab-panel ${panel.className}` : "hl-ctab-panel"}
      >
        <div className="hl-ctab-wash" aria-hidden="true" />
        <div className="hl-ctab-in">
          <h3 className="hl-ctab-h3">{heading}</h3>
          <RouterLink className="hl-cta-btn hl-ctab-btn" to={CTA_TARGET}>
            {button}
            <Arrow
              className={isHebrew ? "hl-cta-arr" : "hl-cta-arr hl-cta-arr-ltr"}
              direction={isHebrew ? "left" : "right"}
            />
          </RouterLink>
        </div>
      </div>
    </SectionShell>
  );
}

/**
 * The final CTA. Every copy prop is OPTIONAL and defaults to today's constant -
 * `Homepage.js` passes none of them, so its render is unchanged.
 *
 * @param {object} props
 * @param {boolean} [props.isHebrew]
 * @param {string|null} [props.eyebrow] an explicit `null` renders NO eyebrow;
 *   left out, the homepage's own is used
 * @param {string} [props.title] the section's <h2>
 * @param {string|null} [props.sub] same rule as the eyebrow: `null` drops the
 *   line entirely, `undefined` keeps the default. A panel reused as a mid-page
 *   ask carries the heading and the button alone.
 * @param {string} [props.ctaLabel] the button's label
 * @param {string} [props.trackSection] engagement section name (see
 *   SectionShell) - "cta-end" unless the page names it; a page with two panels
 *   passes "cta-mid" to the first.
 */
export default function CtaLive({
  isHebrew = true,
  eyebrow,
  title,
  sub,
  ctaLabel,
  trackSection = "cta-end",
}) {
  const t = isHebrew ? COPY.he : COPY.en;
  // `??` would have made an explicit null mean "use the default" - the one
  // thing a caller passing null is asking NOT to happen. Undefined is unset.
  const copy = {
    eyebrow: eyebrow === undefined ? t.eyebrow : eyebrow,
    heading: title ?? t.heading,
    sub: sub === undefined ? t.sub : sub,
    button: ctaLabel ?? t.button,
  };
  const panel = useReveal({ variant: "scale" });

  return (
    <SectionShell
      scene="cta"
      mesh="cloud"
      meshAlpha=".5"
      className="hl-cta"
      trackSection={trackSection}
      // `py` REPLACES the shell's responsive `py` key; a bare `pt` would lose to
      // its md media rule. The prototype: `.cta{padding:80px 0 120px}`, every
      // width - scaled by the owner's round-3 -20% (80 -> 64, 120 -> 96).
      sx={{ py: "64px", pb: "96px" }}
    >
      <div
        ref={panel.ref}
        className={panel.className ? `hl-cta-panel ${panel.className}` : "hl-cta-panel"}
      >
        <div className="hl-cta-aur" aria-hidden="true" />
        <div className="hl-cta-rings" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
        <div className="hl-cta-in">
          {copy.eyebrow ? (
            <div className="hl-eyebrow" style={{ justifyContent: "center" }}>
              {copy.eyebrow}
            </div>
          ) : null}
          <h2 className="hl-h2 hl-h2-center hl-cta-h2">{copy.heading}</h2>
          {copy.sub ? <p className="hl-sub hl-sub-center hl-cta-sub">{copy.sub}</p> : null}
          <RouterLink className="hl-cta-btn" to={CTA_TARGET}>
            {copy.button}
            <Arrow
              className={isHebrew ? "hl-cta-arr" : "hl-cta-arr hl-cta-arr-ltr"}
              direction={isHebrew ? "left" : "right"}
            />
          </RouterLink>
        </div>
      </div>
    </SectionShell>
  );
}
